// @packages
import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  Text
} from 'react-native';
import { useTheme } from '@rneui/themed';
import {
  useNavigate
} from 'react-router-native';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { StatusBar } from 'expo-status-bar';

// @scripts
import List from '../../components/molecules/list';
import LocationService from '../../services/location-service';
import TestingService from '../../services/testing-service';
import useStyles from './styles';
import { config } from '../../core/config';
import { routes } from '../../core/routes/config';
import { sort } from '../../utils/array';
import { useAuth } from '../../core/context/provider';
import { useSite } from '../../core/context/siteProvider';
import { ISession, ISite } from '../../utils/types';

interface IProps {
  locationService?: typeof LocationService;
}

interface ILocation extends ISite {
  siteEnrollment?: ISession
}

const Dashboard = ({ locationService = LocationService }: IProps) => {
  const styles = useStyles();
  const [locations, setLocations] = useState<ILocation[]>([]);
  const [isGettingData, setIsGettingData] = useState<boolean>(true);
  const { theme } = useTheme();
  const { user } = useAuth();
  const { onSelect } = useSite();

  const navigate = useNavigate();

  dayjs.extend(isSameOrBefore);
  dayjs.extend(isSameOrAfter);

  const goToSettings = (item: any) => {
    onSelect({ siteId: item.site_id, siteName: item.name });
    navigate(routes.siteScheduleDetail.path);
  };

  async function fetchLocations(siteIds:number[]) {
    try {
      setIsGettingData(true);
      const [sites, sessions] = await Promise.all([
        Promise.all(siteIds.map((sid:number) => locationService.getSiteDetail(sid))),
        Promise.all(siteIds.map((sid:number) => TestingService.tryGetCurrentSession(sid)))
      ]);
      setLocations(sites.map((site, i) => {
        const siteEnrollment = sessions[i];
        return { ...site, siteEnrollment };
      }));
    } finally {
      setIsGettingData(false);
    }
  }

  useEffect(() => {
    fetchLocations(user?.siteIds ?? []);
  }, user?.siteIds ?? []);

  return (
    <ScrollView
      testID="RNE__LISTITEM__padView"
      contentContainerStyle={styles.container}
      style={{ backgroundColor: theme.colors.background }}
      scrollEnabled={!isGettingData}
    >
      <Text style={styles.titleText}>{config.text.landingPage.title}</Text>
      {locations.length || isGettingData ? (
        <List
          idProp="site_id"
          items={sort(locations as any, 'name')}
          onPress={goToSettings}
          statusProp="siteEnrollment"
          titleProp="name"
        />
      ) : (
        <Text style={styles.noSitesText}>
          {config.text.landingPage.noSites}
        </Text>
      )}

      {isGettingData && <ActivityIndicator size="large" color={theme.colors.grey1} />}
      <StatusBar style="auto" />
    </ScrollView>
  );
};

export default Dashboard;
